import { createClient as createRawClient } from '@supabase/supabase-js';
import { createBrowserClient as createRawBrowserClient, createServerClient as createRawServerClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';
import { applicationProjectRef, databaseScope, scopedClientOptions } from './scope';

export function developmentBucket(name: string): string {
  if (!name || name.includes('/') || name.includes('..')) throw new Error('Invalid development bucket');
  return name.startsWith('development-') ? name : `development-${name}`;
}

const scopedChannels = new WeakSet<object>();
/** Keep the SDK contract while routing storage and realtime through the same boundary. */
export function scopeDevelopmentServices<T extends SupabaseClient>(client: T): T {
  return new Proxy(client, {
    get(target, property, receiver) {
      if (property === 'storage') {
        const storage = target.storage;
        return new Proxy(storage, {
          get(storageTarget, operation, storageReceiver) {
            const original = Reflect.get(storageTarget, operation, storageReceiver);
            if (['from', 'getBucket', 'createBucket', 'updateBucket', 'emptyBucket', 'deleteBucket'].includes(String(operation))) {
              return (bucket: string, ...args: unknown[]) => Reflect.apply(original, storageTarget, [developmentBucket(bucket), ...args]);
            }
            return typeof original === 'function' ? original.bind(storageTarget) : original;
          },
        });
      }
      if (property === 'channel') {
        return (name: string, options?: Parameters<T['channel']>[1]) => {
          const channel = target.channel(`development:${name}`, options);
          if (!scopedChannels.has(channel)) {
            const original = channel.on.bind(channel);
            channel.on = new Proxy(original, {
              apply(on, context, [event, filter, callback]) {
                if (event === 'postgres_changes') {
                  if (filter.schema && !['public', 'development'].includes(filter.schema)) throw new Error('Realtime schema is outside development');
                  filter = { ...filter, schema: 'development' };
                }
                return Reflect.apply(on, context, [event, filter, callback]);
              },
            });
            scopedChannels.add(channel);
          }
          return channel;
        };
      }
      const original = Reflect.get(target, property, receiver);
      return typeof original === 'function' ? original.bind(target) : original;
    },
  });
}

// A call proxy preserves the SDK's generic and cookie overloads exactly. Both
// schemas share the application table contract; only routing/options change.
function scopedFactory<T extends typeof createRawClient | typeof createRawBrowserClient | typeof createRawServerClient>(factory: () => T): T {
  return new Proxy(function () {} as unknown as T, {
    apply(_target, receiver, [url, key, options]) {
      const scoped = scopedClientOptions(url, key, options);
      const client = Reflect.apply(factory(), receiver, [url, key,
        scoped === options ? options : { ...options, ...scoped }]);
      return applicationProjectRef(url) && databaseScope() === 'development' ? scopeDevelopmentServices(client) : client;
    },
  });
}

export const createClient = scopedFactory(() => createRawClient);
export const createBrowserClient = scopedFactory(() => createRawBrowserClient);
export const createServerClient = scopedFactory(() => createRawServerClient);

/** Explicit scope for test runners on hosts whose application runtime is production. */
export const createDevelopmentClient: typeof createRawClient = new Proxy(createRawClient, {
  apply(target, receiver, [url, key, options]) {
    return scopeDevelopmentServices(Reflect.apply(target, receiver,
      [url, key, scopedClientOptions(url, key, options, 'development')]));
  },
});
